import { useRef, useEffect, useState } from 'react';
import { getChargifyConfig, chargifyFields } from '../../conf/chargify';
import { useEnvStore } from '../../store/models';
import { getCurrency } from '../../utils';

function buildFields (panelId) {
    const updated = {};
    Object.entries(chargifyFields).forEach(([key, field]) => {
        updated[key] = { ...field, selector: `#${panelId}` };
    });
    return updated;
}

/*
 * Shared Chargify one-time-token hook. Manages the Chargify instance lifecycle
 * and tokenisation mechanics; callers own their error display and submit guards.
 *
 * Usage:
 *   const { formRef, loading, initChargify, generateToken, unload } = useChargifyOtt({
 *       panelId: 'myChargifyPanel',
 *       onToken: (token) => { ... },
 *       onError: (err) => { ... },
 *   });
 *
 * - Call initChargify(styles) from ChargifyPanel's mount-time useEffect.
 * - Call generateToken() on form submit; it sets loading true/false around the call.
 * - Call unload() when the panel is removed without unmounting (e.g. drawer cancel).
 *   The useEffect cleanup also calls unload on unmount.
 */
function useChargifyOtt ({ panelId, onToken, onError }) {
    const envStore = useEnvStore();
    const chargify = useRef(null);
    const formRef = useRef(null);
    const [loading, setLoading] = useState(false);

    // Keep callbacks in refs so generateToken always calls the latest version
    // without needing to be recreated.
    const onTokenRef = useRef(onToken);
    const onErrorRef = useRef(onError);
    onTokenRef.current = onToken;
    onErrorRef.current = onError;

    useEffect(() => {
        return () => {
            if (chargify.current && chargify.current.unload) {
                chargify.current.unload();
            }
        };
    }, []);

    function initChargify (styles) {
        if (!window.Chargify) return;
        const ccy = getCurrency(envStore);
        const conf = getChargifyConfig(ccy);
        if (!chargify.current) {
            chargify.current = new window.Chargify();
        }
        chargify.current.load({
            publicKey: conf.publicKey,
            serverHost: `https://${conf.endpoint}`,
            selector: `#${panelId}`,
            type: 'card',
            hideCardImage: false,
            styles,
            fields: buildFields(panelId),
        });
    }

    function generateToken () {
        if (!chargify.current || !formRef.current) return;
        setLoading(true);
        chargify.current.token(
            formRef.current,
            (token) => {
                setLoading(false);
                onTokenRef.current(token);
            },
            (err) => {
                setLoading(false);
                onErrorRef.current(err);
            },
        );
    }

    function unload () {
        if (chargify.current && chargify.current.unload) {
            chargify.current.unload();
        }
    }

    return { formRef, loading, initChargify, generateToken, unload };
}

export default useChargifyOtt;
